import React, { useState, useEffect, useRef } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import callApi from "services/api";
import { loadingState } from "global/LoaderState";
import { useRecoilState, useSetRecoilState } from "recoil";
import { AlertState } from "global/AlertState";
import { LoginState } from "global/LoginState";
import { useNavigate } from "react-router-dom";
import { generateSHA512HashAndEncryptInES6 } from "services/encryption";
import Cookies from "js-cookie";
import { generateCaptcha } from "services/CaptchaService";

const ChangePassword = ({ setLoginActiveView }) => {
  const navigate = useNavigate();
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [, enableLoader] = useRecoilState(loadingState);
  const [, setAlertMessage] = useRecoilState(AlertState);
  const setLoginState = useSetRecoilState(LoginState);
  const canvasRef = useRef(null);
  const [captcha, setCaptcha] = useState("");

  const togglePasswordVisibility = (type) => {
    if (type === "old") setShowOldPassword((prev) => !prev);
    if (type === "new") setShowPassword((prev) => !prev);
    if (type === "confirm") setShowConfirmPassword((prev) => !prev);
  };

  useEffect(() => {
    generateCaptcha(setCaptcha, canvasRef);
  }, []);

  const formik = useFormik({
    initialValues: {
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      oldPassword: Yup.string().required("Old password is required."),
      newPassword: Yup.string()
        .required("New password is required.")
        .matches(
          /[A-Z]/,
          "Password must contain at least one uppercase letter.",
        )
        .matches(/[0-9]/, "Password must contain at least one number.")
        .matches(
          /[!@#$%^&*(),.?":{}|<>]/,
          "Password must contain at least one special character.",
        ),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("newPassword"), null], "Passwords must match.")
        .required("Confirm password is required."),
      captchaInput: Yup.string().required("Please enter captcha."),
    }),
    onSubmit: async (values) => {
      if (values.captchaInput !== captcha) {
        setAlertMessage("Invalid captcha. Please try again.");
        generateCaptcha(setCaptcha, canvasRef);
        return;
      }
      const encryptedData = {
        oldPassword: generateSHA512HashAndEncryptInES6(values?.oldPassword),
        newPassword: generateSHA512HashAndEncryptInES6(values?.newPassword),
      };
      enableLoader(true);
      setLoading(true);
      var response = await callApi(
        "/updatePassword",
        "POST",
        encryptedData,
        true,
        true,
        setAlertMessage,
        navigate,
        setLoginState,
      );
      enableLoader(false);
      setLoading(false);
      if (response.success) {
        navigate("/");
        setLoginActiveView("login");
        setAlertMessage(
          "Your password has been updated successfully. Please login again.|true",
        );
        setLoginState(false);
      }
    },
  });

  return (
    <>
      <div className="row justify-content-center">
        <div className="col-md-8">
          <div className="card mt-3">
            <div className="card-face card-front">
              <div className="card-body">
                <h3 className="card-title mb-3">Change Password</h3>
                <form onSubmit={formik.handleSubmit}>
                  {/* Old Password */}
                  <div className="mb-3">
                    <label htmlFor="oldPassword" className="form-label">
                      Old Password
                    </label>
                    <div className="input-group">
                      <input
                        type={showOldPassword ? "text" : "password"}
                        id="oldPassword"
                        name="oldPassword"
                        className={`form-control ${
                          formik.touched.oldPassword &&
                          formik.errors.oldPassword
                            ? "is-invalid"
                            : ""
                        }`}
                        {...formik.getFieldProps("oldPassword")}
                        maxLength={50}
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => togglePasswordVisibility("old")}
                      >
                        {showOldPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                      {formik.touched.oldPassword &&
                        formik.errors.oldPassword && (
                          <div className="invalid-feedback">
                            {formik.errors.oldPassword}
                          </div>
                        )}
                    </div>
                  </div>

                  {/* New Password */}
                  <div className="mb-3">
                    <label htmlFor="password" className="form-label">
                      New Password
                    </label>
                    <div className="input-group">
                      <input
                        type={showPassword ? "text" : "password"}
                        id="password"
                        name="newPassword"
                        className={`form-control ${
                          formik.touched.newPassword &&
                          formik.errors.newPassword
                            ? "is-invalid"
                            : ""
                        }`}
                        {...formik.getFieldProps("newPassword")}
                        maxLength={50}
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => togglePasswordVisibility("new")}
                      >
                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                      {formik.touched.newPassword &&
                        formik.errors.newPassword && (
                          <div className="invalid-feedback">
                            {formik.errors.newPassword}
                          </div>
                        )}
                    </div>
                  </div>

                  {/* Confirm Password */}
                  <div className="mb-3">
                    <label htmlFor="confirmPassword" className="form-label">
                      Confirm Password
                    </label>
                    <div className="input-group">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        id="confirmPassword"
                        name="confirmPassword"
                        className={`form-control ${
                          formik.touched.confirmPassword &&
                          formik.errors.confirmPassword
                            ? "is-invalid"
                            : ""
                        }`}
                        {...formik.getFieldProps("confirmPassword")}
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => togglePasswordVisibility("confirm")}
                      >
                        {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                      {formik.touched.confirmPassword &&
                        formik.errors.confirmPassword && (
                          <div className="invalid-feedback">
                            {formik.errors.confirmPassword}
                          </div>
                        )}
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">
                      Enter Captcha
                      <span className="text-danger">*</span>
                    </label>

                    <div className="d-flex align-items-center gap-2">
                      <canvas
                        ref={canvasRef}
                        style={{
                          border: "1px solid #ccc",
                          background: "#f5f5f5",
                        }}
                      />

                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => generateCaptcha(setCaptcha, canvasRef)}
                      >
                        Refresh
                      </button>
                    </div>

                    <input
                      type="text"
                      name="captchaInput"
                      className={`form-control mt-2 ${
                        formik.touched.captchaInput &&
                        formik.errors.captchaInput
                          ? "is-invalid"
                          : ""
                      }`}
                      placeholder="Enter captcha"
                      autoComplete="off"
                      maxLength={6}
                      {...formik.getFieldProps("captchaInput")}
                    />

                    {formik.touched.captchaInput &&
                      formik.errors.captchaInput && (
                        <div className="invalid-feedback">
                          {formik.errors.captchaInput}
                        </div>
                      )}
                  </div>

                  <div className="d-grid">
                    <button
                      type="submit"
                      className="btn btn-info"
                      disabled={loading}
                    >
                      Change Password
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          {/* <div className="d-flex justify-content-center mt-2">
            <button
              type="button"
              className="btn btn-sm btn-secondary"
              onClick={() => navigate(-1)}
            >
              Back
            </button>
          </div> */}
        </div>
      </div>
    </>
  );
};

export default ChangePassword;
